import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { BookOpen, ChevronRight, Lock } from 'lucide-react';
import { api, ApiError, type BlogCategory, type BlogPost } from '../api';
import { PageLoader } from '../components/ui';
import { SmartCoverMedia } from '../components/SmartCoverMedia';
import { useAuth } from '../auth';
import { FormattedText } from '../lib/simpleMarkup';

export type { BlogPost };

const PAGE_SIZE = 10;

function formatDate(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
}

function SubscribeCta({ compact }: { compact?: boolean }) {
  return (
    <Link
      to="/plans"
      className={`inline-flex items-center justify-center rounded-radius-sm bg-sand text-white font-semibold hover:opacity-90 touch-manipulation ${
        compact ? 'min-h-[40px] px-4 text-sm' : 'min-h-[48px] px-6 text-sm'
      }`}
    >
      Subscribe to keep learning
    </Link>
  );
}

function LockedPostCard({ post }: { post: BlogPost }) {
  const { me } = useAuth();
  const guest = !me;

  return (
    <div className="relative rounded-2xl border border-line overflow-hidden bg-surface select-none">
      <div className="opacity-40 blur-[1px] pointer-events-none">
        <div className="relative bg-teal-soft overflow-hidden">
          {post.coverUrl ? (
            <SmartCoverMedia src={post.coverUrl} kind={post.coverKind} alt="" learnFrame playable={false} poster={post.thumbnailUrl} />
          ) : (
            <div className="aspect-[16/9] w-full flex items-center justify-center">
              <BookOpen className="w-10 h-10 text-teal" />
            </div>
          )}
        </div>
        <div className="p-4 sm:p-5">
          <div className="font-semibold text-ink text-base sm:text-lg">{post.title}</div>
        </div>
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/75 px-4 text-center">
        <Lock className="w-5 h-5 text-ink" />
        <p className="font-heading font-bold text-ink text-sm sm:text-base leading-snug">
          {guest ? 'Log in to read' : 'This one needs a plan'}
        </p>
        {guest ? (
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-radius-sm bg-sand text-white font-semibold hover:opacity-90 touch-manipulation min-h-[40px] px-4 text-sm"
          >
            Log in
          </Link>
        ) : (
          <SubscribeCta compact />
        )}
      </div>
    </div>
  );
}

function OpenPostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blogs/${post.id}`}
      className="group block bg-surface rounded-2xl border border-line overflow-hidden hover:shadow-pop transition-all touch-manipulation"
    >
      <div className="relative bg-teal-soft overflow-hidden">
        {post.coverUrl ? (
          <SmartCoverMedia
            src={post.coverUrl}
            kind={post.coverKind}
            alt={post.title}
            learnFrame
            playable={false}
            poster={post.thumbnailUrl}
          />
        ) : (
          <div className="aspect-[16/9] w-full flex items-center justify-center">
            <BookOpen className="w-10 h-10 text-teal" />
          </div>
        )}
      </div>
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2 text-xs text-ink-faint mb-1">
          <span>{formatDate(post.publishedAt ?? post.createdAt)}</span>
          {post.category?.name && (
            <span className="truncate text-right">{post.category.name}</span>
          )}
        </div>
        <div className="font-semibold text-ink text-base sm:text-lg group-hover:text-teal transition-colors">
          {post.title}
        </div>
        <div className="mt-3 text-xs font-medium text-sand-strong inline-flex items-center gap-1">
          Read more <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}

export function BlogsPage() {
  const { me } = useAuth();
  const [categoryId, setCategoryId] = useState<string | null>(null);

  const categoriesQ = useQuery({
    queryKey: ['blog-categories', me?.id ?? 'guest'],
    queryFn: () => api.get<{ categories: BlogCategory[] }>('/api/blogs/categories'),
  });

  const q = useInfiniteQuery({
    queryKey: ['blogs', categoryId ?? 'all', me?.id ?? 'guest'],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(pageParam),
      });
      if (categoryId) params.set('category', categoryId);
      return api.get<{ posts: BlogPost[]; total: number; access?: 'full' | 'preview' }>(`/api/blogs?${params}`);
    },
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      if (last.access === 'preview') return undefined;
      const loaded = pages.reduce((n, p) => n + p.posts.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
  });

  if (q.isLoading) return <PageLoader />;
  const posts = q.data?.pages.flatMap(p => p.posts) ?? [];
  const access = q.data?.pages[0]?.access ?? 'full';
  const preview = access === 'preview';
  const openPosts = posts.filter(p => !p.locked);
  const lockedPosts = posts.filter(p => p.locked);
  const categories = categoriesQ.data?.categories ?? [];
  const hasMore = !preview && !!q.hasNextPage;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
      <div className="mb-6 sm:mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-2">Learn</h1>
        <p className="text-ink-soft text-sm sm:text-base max-w-2xl">
          {preview
            ? 'Three lessons to start. The rest needs a plan.'
            : 'Guides for clinic weeks and questions.'}
        </p>
        {!preview && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={() => setCategoryId(null)}
              className={`min-h-[40px] px-3.5 rounded-radius-sm text-sm font-medium touch-manipulation transition-colors ${
                categoryId === null
                  ? 'bg-sand text-white'
                  : 'bg-surface border border-line text-ink-soft hover:text-ink'
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategoryId(cat.id)}
                className={`min-h-[40px] px-3.5 rounded-radius-sm text-sm font-medium touch-manipulation transition-colors ${
                  categoryId === cat.id
                    ? 'bg-sand text-white'
                    : 'bg-surface border border-line text-ink-soft hover:text-ink'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {posts.length === 0 ? (
        <div className="max-w-lg mx-auto text-center py-16">
          <div className="w-16 h-16 rounded-full bg-teal-soft flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-7 h-7 text-teal" />
          </div>
          <h2 className="font-heading text-xl font-bold text-ink mb-2">No posts yet</h2>
          <p className="text-ink-soft text-sm">Check back soon for new learning guides.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {openPosts.map(post => (
              <OpenPostCard key={post.id} post={post} />
            ))}
          </div>
          {lockedPosts.length > 0 && (
            <div className="mt-10">
              <div className="mb-4 max-w-xl">
                <h2 className="font-heading text-xl font-bold text-ink">The rest needs a plan</h2>
                <p className="text-ink-soft text-sm mt-1">
                  Three lessons to start. Subscribe if you want the rest.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {lockedPosts.map(post => (
                  <LockedPostCard key={post.id} post={post} />
                ))}
              </div>
            </div>
          )}
          {hasMore && (
            <div className="mt-8">
              {q.isFetchingNextPage ? (
                <PageLoader variant="inline" />
              ) : (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => void q.fetchNextPage()}
                    className="inline-flex items-center justify-center min-h-[48px] px-6 rounded-radius-sm bg-sand text-white text-sm font-semibold touch-manipulation"
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function BlogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery({
    queryKey: ['blog', id],
    queryFn: () => api.get<{ post: BlogPost }>(`/api/blogs/${id}`),
    enabled: !!id,
    retry: false,
  });

  if (q.isLoading) return <PageLoader variant="detail" />;

  const blocked = q.error instanceof ApiError
    && (q.error.code === 'subscription_required' || q.error.status === 403);

  if (blocked) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-teal-soft flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-teal" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">This one needs a plan</h1>
        <p className="text-ink-soft text-sm mb-6">
          Subscribe to read this guide.
        </p>
        <SubscribeCta />
        <div className="mt-4">
          <Link to="/blogs" className="text-sand-strong hover:underline text-sm">← Back to Learn</Link>
        </div>
      </div>
    );
  }

  if (!q.data?.post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">Post not found</h1>
        <Link to="/blogs" className="text-sand-strong hover:underline text-sm">← Back to Learn</Link>
      </div>
    );
  }

  const post = q.data.post;

  return (
    <article className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
      <Link to="/blogs" className="text-sm text-sand-strong font-medium hover:underline">← Learn</Link>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink mt-3 mb-2">{post.title}</h1>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-faint mb-6">
        <span>{formatDate(post.publishedAt ?? post.createdAt)}</span>
        {post.category?.name && <span>{post.category.name}</span>}
      </div>
      <FormattedText text={post.body} className="text-base text-ink break-words" />
      {post.coverUrl && (
        <div className="rounded-2xl overflow-hidden bg-teal-soft mt-6 sm:mt-8">
          <SmartCoverMedia
            src={post.coverUrl}
            kind={post.coverKind}
            alt={post.title}
            learnFrame
            poster={post.thumbnailUrl}
          />
        </div>
      )}
    </article>
  );
}
